/* =========================================================================
   Penny Premium 3D — facets.js
   Collection filtering / sorting / pagination over the Section Rendering
   API. Progressive enhancement only: without JS the facet form submits
   natively and standard pagination links render.

   Swap regions (server-rendered in main-collection, morphed on update):
     [data-facets-drawer-content]  filter groups (counts update)
     [data-active-pills]           active filter pills row
     [data-grid-container]         product grid + load-more/pagination
     [data-toolbar-count]          "128 products"
   Other contracts: [data-facet-form], [data-sort-select], pills carry
   [data-filter-remove] hrefs, [data-load-more], [data-results-count]
   (hidden raw number), [data-apply-count] on the drawer's apply button.
   ========================================================================= */

const penny = window.penny;
const motionFull = () => document.documentElement.classList.contains('motion-full');

const root = document.querySelector('[data-collection-root]');

/* Reveal server-rendered .reveal nodes that arrive after the initial
   observer pass (swapped or appended content). */
function revealNew(scope) {
  scope.querySelectorAll('.reveal:not(.is-revealed)').forEach((el, i) => {
    el.style.setProperty('--reveal-index', String(Math.min(i, 4)));
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-revealed')));
  });
}

if (root) {
  const sectionId = root.dataset.sectionId;
  const form = root.querySelector('[data-facet-form]');
  const drawer = root.querySelector('[data-facets-drawer]');
  const openButton = root.querySelector('[data-facets-open]');
  const overlay = root.querySelector('[data-facets-overlay]');
  const announce = root.querySelector('[data-results-announce]');
  const pillsRegion = root.querySelector('[data-active-pills]');
  let liveCountTimer = null;
  let autoLoads = 0;

  /* --- Drawer ------------------------------------------------------------ */
  const openDrawer = () => {
    drawer.removeAttribute('hidden');
    overlay?.removeAttribute('hidden');
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      overlay?.classList.add('is-open');
    });
    openButton.setAttribute('aria-expanded', 'true');
    document.body.classList.add('scroll-lock');
    penny.trapFocus(drawer, drawer.querySelector('[data-facets-close]'));
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    openButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('scroll-lock');
    penny.releaseFocus();
    const done = () => {
      drawer.setAttribute('hidden', '');
      overlay?.setAttribute('hidden', '');
    };
    if (!motionFull()) done();
    else drawer.addEventListener('transitionend', done, { once: true });
  };

  openButton?.addEventListener('click', openDrawer);
  overlay?.addEventListener('click', closeDrawer);
  drawer?.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });
  drawer?.querySelectorAll('[data-facets-close]').forEach((b) => b.addEventListener('click', closeDrawer));

  /* --- URL building + fetch ---------------------------------------------- */
  const buildUrl = () => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value !== '') params.append(key, value);
    });
    const query = params.toString();
    return form.getAttribute('action') + (query ? `?${query}` : '');
  };

  const fetchSection = async (url) => {
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}section_id=${sectionId}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const swapRegion = (doc, selector) => {
    const target = root.querySelector(selector);
    const fresh = doc.querySelector(selector);
    if (target && fresh) target.replaceChildren(...fresh.childNodes);
  };

  const applyDensity = () => {
    let density = null;
    try {
      density = localStorage.getItem('penny:grid-density');
    } catch (_) {
      /* fine */
    }
    const grid = root.querySelector('[data-grid]');
    if (grid && density) grid.dataset.density = density;
  };

  const applyUrl = async (url, { push = true, focusPills = false } = {}) => {
    root.classList.add('is-loading');
    try {
      const doc = await fetchSection(url);
      ['[data-grid-container]', '[data-toolbar-count]', '[data-active-pills]', '[data-facets-drawer-content]'].forEach(
        (sel) => swapRegion(doc, sel)
      );
      applyDensity();
      autoLoads = 0;
      if (push) window.history.pushState({}, '', url);
      const count = root.querySelector('[data-results-count]')?.textContent.trim();
      if (announce && count) {
        announce.textContent = announce.dataset.template.replace('[count]', count);
      }
      revealNew(root.querySelector('[data-grid-container]'));
      if (focusPills) pillsRegion?.focus();
      initPriceRanges();
      document.dispatchEvent(new Event('shopify:section:load'));
    } catch (_) {
      window.location.href = url; // graceful full navigation
    } finally {
      root.classList.remove('is-loading');
    }
  };

  /* --- Live result count on the apply button (grid swaps on apply only) --- */
  const refreshLiveCount = () => {
    window.clearTimeout(liveCountTimer);
    liveCountTimer = window.setTimeout(async () => {
      try {
        const doc = await fetchSection(buildUrl());
        const count = doc.querySelector('[data-results-count]')?.textContent.trim();
        const applyButton = root.querySelector('[data-apply-count]');
        if (count && applyButton) {
          applyButton.textContent = applyButton.dataset.template.replace('[count]', count);
        }
      } catch (_) {
        /* count refresh is best-effort */
      }
    }, 350);
  };

  form?.addEventListener('change', (event) => {
    if (event.target.closest('[data-sort-select]')) {
      applyUrl(buildUrl());
      return;
    }
    refreshLiveCount();
  });

  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    closeDrawer();
    applyUrl(buildUrl(), { focusPills: true });
  });

  /* --- Active pills / clear links (server-rendered hrefs) ----------------- */
  root.addEventListener('click', (event) => {
    const pill = event.target.closest('[data-filter-remove]');
    if (pill) {
      event.preventDefault();
      applyUrl(pill.getAttribute('href'), { focusPills: true });
    }
  });

  /* --- Back/forward ------------------------------------------------------- */
  window.addEventListener('popstate', () => {
    applyUrl(window.location.href, { push: false });
  });

  /* --- Load more + optional infinite scroll ------------------------------- */
  const loadMore = async (button) => {
    const url = button.dataset.loadMore;
    button.disabled = true;
    button.classList.add('is-waiting');
    try {
      const doc = await fetchSection(url);
      const grid = root.querySelector('[data-grid]');
      const newItems = Array.from(doc.querySelectorAll('[data-grid] > li'));
      newItems.forEach((li) => grid.append(li));
      const container = root.querySelector('[data-load-more-container]');
      const fresh = doc.querySelector('[data-load-more-container]');
      if (container && fresh) container.replaceChildren(...fresh.childNodes);
      window.history.replaceState({}, '', url);
      revealNew(grid);
      const first = newItems[0]?.querySelector('a');
      first?.focus({ preventScroll: true });
      document.dispatchEvent(new Event('shopify:section:load'));
    } catch (_) {
      window.location.href = url;
    }
  };

  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-load-more]');
    if (!button) return;
    autoLoads = 0; // a manual click re-arms auto-loading
    loadMore(button);
  });

  if (root.dataset.infiniteScroll === 'true') {
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || autoLoads >= 2) return;
        const button = root.querySelector('[data-load-more]');
        if (button && !button.disabled) {
          autoLoads += 1;
          loadMore(button);
        }
      });
    }).observe(root.querySelector('[data-load-more-sentinel]'));
  }

  /* --- Dual-thumb price range --------------------------------------------- */
  function initPriceRanges() {
    root.querySelectorAll('[data-price-range]').forEach((range) => {
      if (range.dataset.ready) return;
      range.dataset.ready = 'true';
      const [lo, hi] = range.querySelectorAll('input[type="range"]');
      const [loNum, hiNum] = range.querySelectorAll('input[type="number"]');
      const track = range.querySelector('[data-price-range-fill]');

      const paint = () => {
        const max = Number(lo.max) || 1;
        track.style.insetInlineStart = `${(Number(lo.value) / max) * 100}%`;
        track.style.insetInlineEnd = `${100 - (Number(hi.value) / max) * 100}%`;
      };

      const sync = (source) => {
        if (Number(lo.value) > Number(hi.value)) {
          if (source === lo) hi.value = lo.value;
          else lo.value = hi.value;
        }
        loNum.value = lo.value === lo.min ? '' : lo.value;
        hiNum.value = hi.value === hi.max ? '' : hi.value;
        paint();
      };

      lo.addEventListener('input', () => sync(lo));
      hi.addEventListener('input', () => sync(hi));
      loNum.addEventListener('change', () => {
        lo.value = loNum.value || lo.min;
        sync(lo);
        refreshLiveCount();
      });
      hiNum.addEventListener('change', () => {
        hi.value = hiNum.value || hi.max;
        sync(hi);
        refreshLiveCount();
      });
      [lo, hi].forEach((input) =>
        input.addEventListener('change', refreshLiveCount)
      );
      paint();
    });
  }

  initPriceRanges();

  /* --- Collapsible collection description -------------------------------- */
  const readMore = root.querySelector('[data-read-more]');
  readMore?.addEventListener('click', () => {
    const target = root.querySelector('[data-read-more-target]');
    const expanded = target.classList.toggle('is-expanded');
    readMore.setAttribute('aria-expanded', String(expanded));
    readMore.querySelector('[data-read-more-label-more]').toggleAttribute('hidden', expanded);
    readMore.querySelector('[data-read-more-label-less]').toggleAttribute('hidden', !expanded);
  });

  /* --- Grid density toggle (persisted; pre-applied by an inline one-liner
         in the section so there's no flash) ------------------------------- */
  root.querySelectorAll('[data-density-button]').forEach((button) => {
    button.addEventListener('click', () => {
      const density = button.dataset.densityButton;
      root.querySelector('[data-grid]').dataset.density = density;
      try {
        localStorage.setItem('penny:grid-density', density);
      } catch (_) {
        /* fine */
      }
      root.querySelectorAll('[data-density-button]').forEach((b) => {
        b.setAttribute('aria-pressed', String(b === button));
      });
    });
  });
}
